import { useTranslation } from 'react-i18next';

import { App, Modal } from 'antd';
import qs from 'qs';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileLimitationEnum } from '../../enums/FileLimitationEnum';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { FileTypeEnum } from '../../enums/FileTypeEnum';
import type File from '../../interfaces/File';
import type { OpenFileLocallyPayload } from '../../interfaces/FileApiService';
import { toAbsoluteServiceUrl } from '../../lib/absoluteServiceUrl';
import { getOfficePath, isOfficeEditableType } from '../../lib/officeRoutePath';
import { PREVIEW_PUSHED_STATE, buildPreviewPath } from '../../lib/previewRoutePath';
import { FileService } from '../../services/fileApiService';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../../store/store';
import { getAntivirusInfectedMessage, isAntivirusBlocked } from '../../utils/antivirus';
import { openSecretFileModal } from '../../utils/openSecretFileModal';
import { copyToClipboard, hasFileLimitation, isSecretFile } from '../../utils/utils';
import { useAuth } from '../useAuth';
import { useFileDownload } from '../useFileDownload';
import { usePermissions } from '../usePermissions';

interface RedirectFileToLocationProps {
  file: File;
  avoidOpenViewer?: boolean;
  queryParams?: Record<string, string>;
  targetTable?: FilesTableEnum;
}

export type OpenFileSafelyOptions = {
  onBlankPage?: boolean;
  skipLocalDefaultOpening?: boolean;
  /** Context-menu Open: force `/viewer` for secrets (click uses modal). */
  forceViewer?: boolean;
};

const fileService = new FileService();

function useOpenFile() {
  const { modal } = App.useApp();
  const { t } = useTranslation();
  const { authenticatedUser } = useAuth();
  const token = useAppStore((s) => s.token);
  const refreshToken = useAppStore((s) => s.customTokens?.rToken);
  const {
    getFilePasswordSession,
    setPendingFilePasswordRequest,
    setFileIdToNavigateAfterPassword,
  } = useAppStore(
    useShallow((s) => ({
      getFilePasswordSession: s.getFilePasswordSession,
      setPendingFilePasswordRequest: s.setPendingFilePasswordRequest,
      setFileIdToNavigateAfterPassword: s.setFileIdToNavigateAfterPassword,
    })),
  );
  const { decreaseReceivedUnreadCountBy } = usePermissions();

  const markUnreadFileAsReadAndRefreshBadge = useCallback(
    (f: File) => {
      if (!f.isUnRead) return;
      void fileService
        .markFilesAsRead([f.id])
        .then((res) => {
          decreaseReceivedUnreadCountBy(res.updated);
          useAppStore.getState().events.emit.filesMarkedAsReadLocally({ fileIds: [f.id] });
        })
        .catch(() => {});
    },
    [decreaseReceivedUnreadCountBy],
  );
  const { handleDownloadFiles } = useFileDownload();
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /**
   * Open a .fgsecret: modal by default, full viewer when `forceViewer`, protected route when
   * DenyFileScreenCapture requires it. Always skips local-default opening.
   */
  const openSecretFile = useCallback(
    (file: File, { forceViewer = false }: OpenFileSafelyOptions = {}) => {
      markUnreadFileAsReadAndRefreshBadge(file);

      if (hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileScreenCapture)) {
        window.open(`/file/protected/${file.id}`, '_blank')?.focus();
        return true;
      }

      if (forceViewer) {
        // Context-menu “Open” for secrets: always full `/viewer` in a new tab.
        window.open(`/viewer/${file.id}`, '_blank')?.focus();
        return true;
      }

      openSecretFileModal(modal, file);
      return true;
    },
    [markUnreadFileAsReadAndRefreshBadge, modal],
  );

  const getTableForFile = useCallback((file: File, currentUserId: number) => {
    if (file.ownerId !== currentUserId) return FilesTableEnum.Received;
    if (file.isDeleted) return FilesTableEnum.Trash;
    if (file.isArchived) return FilesTableEnum.Hidden;
    if (file.isFavourite) return FilesTableEnum.Favorites;
    return file.ownerId === currentUserId ? FilesTableEnum.Personal : FilesTableEnum.Received;
  }, []);

  const navigateToPreview = useCallback(
    (file: File) => {
      const me = authenticatedUser?.id;
      if (!me) return;

      useAppStore.getState().setViewerPreviewMeta({ fileId: file.id, file });
      if (pathname.startsWith('/files/')) {
        navigate(buildPreviewPath(pathname, file.id), { state: PREVIEW_PUSHED_STATE });
        return;
      }
      const table = getTableForFile(file, me);
      const listBase = file.parentId
        ? `/files/${table}/folder/${file.parentId}`
        : `/files/${table}`;
      navigate(buildPreviewPath(listBase, file.id));
    },
    [authenticatedUser?.id, getTableForFile, navigate, pathname],
  );

  /**
   * A function to safely open a file with specific checks for user authentication,
   * file tagging, and enforced modes.
   *
   * @param file - The file object to be opened.
   * @param onBlankPageOrOptions - Legacy boolean (blank tab) or options object.
   * @param skipLocalDefaultOpening - Legacy 3rd arg when 2nd is boolean.
   */
  const openFileSafely: (
    file: File,
    onBlankPageOrOptions?: boolean | OpenFileSafelyOptions,
    skipLocalDefaultOpening?: boolean,
  ) => boolean = useCallback(
    (
      file: File,
      onBlankPageOrOptions?: boolean | OpenFileSafelyOptions,
      skipLocalDefaultOpeningArg: boolean = false,
    ) => {
      const options: OpenFileSafelyOptions =
        typeof onBlankPageOrOptions === 'object'
          ? onBlankPageOrOptions
          : onBlankPageOrOptions === undefined
            ? {}
            : {
                onBlankPage: onBlankPageOrOptions,
                skipLocalDefaultOpening: skipLocalDefaultOpeningArg,
              };
      // In-app open → preview modal (classic or Guest). Pass `onBlankPage: true` for full `/viewer` tab.
      // Office always opens a dedicated route tab (never modal).
      const { onBlankPage = false, skipLocalDefaultOpening = false, forceViewer = false } = options;

      if (!authenticatedUser?.id || !file.cufi || !file.id) {
        return false;
      }

      if (authenticatedUser.isAntivirusEnabled && !file.isFolder && isAntivirusBlocked(file)) {
        notification.error({
          title: getAntivirusInfectedMessage(t, {
            threatName: file.avThreatName,
            fileName: file.fileName,
          }),
        });
        return false;
      }

      // Secrets: never local-default; click → modal, context Open → viewer (unless capture denied).
      if (!file.isFolder && isSecretFile(file)) {
        return openSecretFile(file, { onBlankPage, forceViewer });
      }

      if (
        !skipLocalDefaultOpening &&
        !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyLocalDefaultOpening)
      ) {
        const payload: OpenFileLocallyPayload = {
          webApp: window.location.origin,
          server: toAbsoluteServiceUrl(import.meta.env.VITE_APP_API_BASE_URL),
          fileId: file.id,
          token: token || '',
          refreshToken: refreshToken || '',
        };
        markUnreadFileAsReadAndRefreshBadge(file);
        fileService.openFileLocally(payload).catch(() => {
          Modal.warning({
            title: t('modal.openFileLocallyError.title'),
            content: t('modal.openFileLocallyError.description'),
            centered: true,
          });
        });
        return false;
      }

      // Check if the file size is greater than 250mb
      if (file.fileSize > 262144000 && file.fileType !== FileTypeEnum.Video) {
        const modalInstance = modal.confirm({
          title: t('modal.fileSizeTooBigForOpen.title'),
          content: t('modal.fileSizeTooBigForOpen.description', {
            value: '250MB',
          }),
          onOk: () => {
            handleDownloadFiles([file]);
          },
          onCancel: () => {
            modalInstance.destroy();
          },
        });

        return false;
      }

      // OTP / Viewer codes are for files only (folders → 400 from /myfiles/otp).
      if (
        !file.isFolder &&
        hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileScreenCapture)
      ) {
        markUnreadFileAsReadAndRefreshBadge(file);
        window.open(`/file/protected/${file?.id}`, '_blank')?.focus();
        return true;
      }

      markUnreadFileAsReadAndRefreshBadge(file);

      // Office (Word/Excel/PowerPoint/Wopitest): always a new tab on the office route.
      if (!file.isFolder && isOfficeEditableType(file.fileType)) {
        window.open(getOfficePath(file.id, file.fileType), '_blank')?.focus();
        return true;
      }

      // Classic or Guest: preview URL opens the modal; `onBlankPage` keeps full `/viewer` tab.
      if (!onBlankPage) {
        navigateToPreview(file);
        return true;
      }

      window.open(`/viewer/${file.id}`, '_blank')?.focus();

      return true;
    },
    [
      authenticatedUser?.id,
      authenticatedUser?.isAntivirusEnabled,
      handleDownloadFiles,
      markUnreadFileAsReadAndRefreshBadge,
      modal,
      notification,
      navigateToPreview,
      openSecretFile,
      refreshToken,
      t,
      token,
    ],
  );

  /**
   * Handles redirecting a file or folder to the appropriate location based on its state and properties.
   */
  const redirectFileToLocation = ({
    file,
    avoidOpenViewer = false,
    queryParams = {},
    targetTable,
  }: RedirectFileToLocationProps) => {
    const me = authenticatedUser?.id!;
    let targetPath: string;

    if (file.isFolder || avoidOpenViewer) {
      if (
        file.isFolder &&
        file.isPasswordSet &&
        !file.isPasswordBypass &&
        !getFilePasswordSession(file.id)
      ) {
        setFileIdToNavigateAfterPassword(file.id);
        setPendingFilePasswordRequest({
          fileId: file.id,
          fileName: file.fileName,
        });
        return;
      }
      // Use targetTable if provided, otherwise determine from file properties
      const table = targetTable || getTableForFile(file, me);
      const folderId = file.isFolder ? file.id : file.parentId;
      targetPath = folderId ? `/files/${table}/folder/${folderId}` : `/files/${table}`;
    } else if (file.isDeleted) {
      targetPath = `/files/${FilesTableEnum.Trash}`;
    } else {
      // file "normale"
      return openFileSafely(file);
    }

    const stringifiesParams = qs.stringify(queryParams, { skipNulls: true });

    if (window.location.pathname === targetPath) {
      notification.info({
        title: t('notifications.fileLocatedInThisLocation'),
        showProgress: true,
        pauseOnHover: true,
      });
    } else {
      navigate(`${targetPath}?${stringifiesParams}`, { replace: true });
    }
  };

  /**
   * Copy the file link to the clipboard
   * @param file
   * @returns
   */
  const copyFileLinkToClipboard = (file: File) => {
    const link = `${window.location.origin}/file/${file.id}`;
    copyToClipboard(link);
  };

  return {
    openFileSafely,
    openSecretFile,
    redirectFileToLocation,
    getTableForFile,
    copyFileLinkToClipboard,
  };
}

export default useOpenFile;
